import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ChakraProvider, Box, Spinner, VStack } from "@chakra-ui/react";
import customTheme from "./theme";
import { useKeycloak } from "@react-keycloak/web";
import { UserProvider, useUser } from "./contexts/UserContext";
import { isEmbedded } from "./EmbedAuthProvider";
import AppContent from "./AppContent";
import ConversationsPage from "./components/ConversationsPage";
import AudioSnippetsPage from "./components/AudioSnippetsPage";
import AudioSnippetDetailPage from "./components/AudioSnippetDetailPage";
import AnalysisPage from "./components/AnalysisPage";
import AdminPage from "./components/AdminPage";
import EditSignatureObjectView from "./components/EditSignatureObjectView";
import AudioBatchesPage from "./components/AudioBatchesPage";
import ThreadsListPage from "./components/ThreadsListPage";
import ThreadDetailPage from "./components/ThreadDetailPage";
import UiSpikeConversationsPage from "./components/UiSpikeConversationsPage";
import ConversationDetailPage from "./components/ConversationDetailPage";
import TestAudioFilesPage from "./components/TestAudioFilesPage";
import EmbedAnalysisView from "./components/embed/EmbedAnalysisView";
import EmbedTranscriptView from "./components/embed/EmbedTranscriptView";
import EmbedLiveTranscriptView from "./components/embed/EmbedLiveTranscriptView";
import EmbedConversationViewer from "./components/embed/EmbedConversationViewer";
import EngagementDashboard from "./components/manager/EngagementDashboard";
import EmployeeEngagementPage from "./components/manager/EmployeeEngagementPage";
import ManagerThreadReviewPage from "./components/manager/ManagerThreadReviewPage";
import CoachingDimensionDetailPage from "./components/manager/CoachingDimensionDetailPage";
import AssociateCoachingDashboard from "./components/associate/AssociateCoachingDashboard";
import NonManagerRoute from "./components/NonManagerRoute";
import { resolveDefaultLanding } from "./utils/defaultLanding";

import "./App.css";

// Routes available inside the iOS WebView shell. Chrome-stripped (no
// sidebar / top nav) — the native shell already owns that real estate.
// See PRD §15.5 (Conversation Detail Analysis) and §15.6 (Live Transcript).
function EmbedApp() {
  return (
    <ChakraProvider theme={customTheme}>
      <Router>
        <Routes>
          <Route
            path="/embed/thread/:threadId/analysis"
            element={<EmbedAnalysisView />}
          />
          <Route
            path="/embed/conversation/:conversationId/transcript"
            element={<EmbedTranscriptView />}
          />
          {/* Option-B iOS conversation viewer (SPIKE): CLEANED-only,
              virtualized transcript + ranged-<audio> waveform player.
              See docs/design-ios-conversation-viewer.md §4/§8. */}
          <Route
            path="/embed/conversation/:conversationId/viewer"
            element={<EmbedConversationViewer />}
          />
          <Route
            path="/embed/conversation/:conversationId/live_transcript"
            element={<EmbedLiveTranscriptView />}
          />
        </Routes>
      </Router>
    </ChakraProvider>
  );
}

function DefaultLanding() {
  const { loading, isAdmin, userInfo } = useUser();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bg="gray.50"
      >
        <VStack spacing={4}>
          <Spinner
            thickness="4px"
            speed="0.65s"
            emptyColor="gray.200"
            color="blue.500"
            size="xl"
          />
        </VStack>
      </Box>
    );
  }

  const landing = resolveDefaultLanding({
    isAdmin,
    role: userInfo?.role,
    managerHome: userInfo?.manager_home,
  });
  if (landing === "admin") return <AudioBatchesPage />;
  if (landing === "engagement") return <EngagementDashboard />;
  return <UiSpikeConversationsPage />;
}

function KeycloakApp() {
  const { keycloak, initialized } = useKeycloak();

  return (
    <ChakraProvider theme={customTheme}>
      <Router>
        {/* Show loading spinner while authenticating, but keep Router mounted */}
        {!initialized || !keycloak.authenticated || !keycloak.token ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100vh"
            bg="gray.50"
          >
            <VStack spacing={4}>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </VStack>
          </Box>
        ) : (
          <UserProvider>
            <Routes>
              {/* Default landing */}
              <Route path="/" element={<DefaultLanding />} />
              {/* Previous landing (kept for later) */}
              {/* <Route path="/" element={<AppContent />} /> */}
              {/* Alternate path to access old landing without deleting it */}
              <Route path="/demo" element={<NonManagerRoute><AppContent /></NonManagerRoute>} />

              {/* Manager engagement and read-only advisor review. */}
              <Route path="/manager/engagement" element={<EngagementDashboard />} />
              <Route
                path="/manager/employees/:employeeId/engagement"
                element={<EmployeeEngagementPage />}
              />
              <Route
                path="/manager/employees/:employeeId/threads/:threadId"
                element={<ManagerThreadReviewPage />}
              />
              <Route
                path="/manager/threads/:threadId"
                element={<ManagerThreadReviewPage />}
              />
              <Route
                path="/manager/coaching/dimensions/:dimensionType/:dimensionId"
                element={<CoachingDimensionDetailPage />}
              />

              {/* Threads list + detail (desktop Threads tab) */}
              <Route path="/coaching" element={<NonManagerRoute><AssociateCoachingDashboard /></NonManagerRoute>} />
              <Route path="/threads" element={<NonManagerRoute><ThreadsListPage /></NonManagerRoute>} />
              <Route
                path="/threads/:threadId"
                element={<NonManagerRoute><ThreadDetailPage /></NonManagerRoute>}
              />

              {/* Disposable UI spike for the non-admin conversation home. */}
              <Route
                path="/ui-spike/conversations"
                element={<NonManagerRoute><UiSpikeConversationsPage /></NonManagerRoute>}
              />

              {/* Per-recording detail (transcript + metadata).
                  Sibling to legacy `/analysis/:conversation_id`, which is
                  still wired up below for the Demo/UNTUCKit/Arcteryx flows. */}
              <Route
                path="/conversations/:conversationId"
                element={<NonManagerRoute><ConversationDetailPage /></NonManagerRoute>}
              />

              {/* Conversations list */}
              <Route path="/conversations" element={<NonManagerRoute><ConversationsPage /></NonManagerRoute>} />

              {/* Audio Snippets list */}
              <Route path="/audio-snippets" element={<NonManagerRoute><AudioSnippetsPage /></NonManagerRoute>} />

              {/* Audio Snippet detail view */}
              <Route
                path="/audio-snippets/:id"
                element={<NonManagerRoute><AudioSnippetDetailPage /></NonManagerRoute>}
              />

              {/* Analysis view */}
              <Route
                path="/analysis/:conversation_id"
                element={<NonManagerRoute><AnalysisPage /></NonManagerRoute>}
              />

              {/* Admin panel */}
              <Route path="/admin" element={<AdminPage />} />

              {/* Audio batches (user-scoped) */}
              <Route path="/audio-batches" element={<NonManagerRoute><AudioBatchesPage /></NonManagerRoute>} />

              {/* Test Audio Files (dev-only / Ethosphere-org; gated server-side) */}
              <Route path="/test-audio" element={<NonManagerRoute><TestAudioFilesPage /></NonManagerRoute>} />

              {/* Signature management - single page for create/edit/view */}
              <Route
                path="/edit-signature"
                element={<EditSignatureObjectView />}
              />
            </Routes>
          </UserProvider>
        )}
      </Router>
    </ChakraProvider>
  );
}

function App() {
  // Embed-mode (PRD §7.3): when iOS injected `window.__ETHOSPHERE_TOKEN__`
  // before mount, skip the Keycloak provider entirely. `useKeycloak()` is
  // only safe to call when wrapped in `<ReactKeycloakProvider>`, so the
  // two paths MUST be separate component trees.
  return isEmbedded() ? <EmbedApp /> : <KeycloakApp />;
}

export default App;
